import { useRef, useCallback, useEffect, useState, type MutableRefObject } from 'react';
import type { Camera } from '../types';
import { clamp, screenToWorld } from '../utils';

const DEFAULT_GRID_SIZE = 70;

/** Names of the 6 canvas layers */
export type LayerName = 'map' | 'grid' | 'token' | 'drawing' | 'fog' | 'ui';

/** Dirty-flags for each canvas layer */
export interface DirtyFlags {
  map: boolean;
  grid: boolean;
  token: boolean;
  drawing: boolean;
  fog: boolean;
  ui: boolean;
}

/** Initial state — all layers dirty */
const INITIAL_DIRTY_FLAGS: DirtyFlags = {
  map: true,
  grid: true,
  token: true,
  drawing: true,
  fog: true,
  ui: true,
};

/** Render callback bound to a specific layer */
export interface LayerCallback {
  layer: LayerName;
  render: () => void;
}

export interface CanvasRefs {
  map: HTMLCanvasElement | null;
  grid: HTMLCanvasElement | null;
  token: HTMLCanvasElement | null;
  drawing: HTMLCanvasElement | null;
  fog: HTMLCanvasElement | null;
  ui: HTMLCanvasElement | null;
}

export interface CanvasEngine {
  camera: Camera;
  /** Always-fresh camera ref — use in render callbacks instead of `camera` state */
  cameraRef: React.MutableRefObject<Camera>;
  gridSize: number;
  mapWidth: number;
  mapHeight: number;
  /** CSS-pixel width of the canvas container */
  width: number;
  /** CSS-pixel height of the canvas container */
  height: number;
  /** Device pixel ratio — use to scale offscreen canvases for HiDPI */
  dpr: number;
  /** Incremented each time DPR changes — use in useEffect deps to invalidate caches */
  dprVersion: number;
  refs: React.MutableRefObject<CanvasRefs>;
  containerRef: React.RefCallback<HTMLDivElement>;
  setCamera: (cam: Camera) => void;
  /** @deprecated Use markDirty / markAllDirty instead. Kept for backward compatibility. */
  render: () => void;
  /** Mark a specific layer as dirty and schedule a RAF render */
  markDirty: (layer: LayerName) => void;
  /** Mark all layers as dirty and schedule a RAF render */
  markAllDirty: () => void;
  /** Alias for markAllDirty — schedule full re-render on next animation frame */
  requestRender: () => void;
  loadMap: (src: string) => Promise<void>;
  fitToView: () => void;
  setZoom: (zoom: number) => void;
  mouseToWorld: (e: React.MouseEvent | MouseEvent | React.PointerEvent | PointerEvent) => { x: number; y: number };
  snapToGridCenter: (wx: number, wy: number) => { x: number; y: number };
  mapImage: HTMLImageElement | null;
  renderCallbacks: React.MutableRefObject<LayerCallback[]>;
  /** Mutable ref for page-level grid size — set from AppShell when active page changes */
  gridSizeRef: MutableRefObject<number>;
  /** Mutable ref for page-level map scale — set from AppShell when active page changes */
  mapScaleRef: MutableRefObject<number>;
  /** Mutable ref for page-level canvas background color */
  bgColorRef: MutableRefObject<string | null>;
  /** Mutable ref for grid style — set from SessionSettings */
  gridStyleRef: MutableRefObject<string>;
  /** Mutable ref for grid opacity (0-100) — set from SessionSettings */
  gridOpacityRef: MutableRefObject<number>;
}

export function useCanvasEngine(): CanvasEngine {
  const containerNodeRef = useRef<HTMLDivElement | null>(null);
  const [containerNode, setContainerNode] = useState<HTMLDivElement | null>(null);
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    containerNodeRef.current = node;
    setContainerNode(node);
  }, []);
  const refs = useRef<CanvasRefs>({ map: null, grid: null, token: null, drawing: null, fog: null, ui: null });
  const [camera, setCameraState] = useState<Camera>({ zoom: 1, offsetX: 0, offsetY: 0 });
  const cameraRef = useRef(camera);
  const mapImageRef = useRef<HTMLImageElement | null>(null);
  const mapLoadGenRef = useRef(0); // generation counter to cancel stale loads
  const mapWidthRef = useRef(2000);
  const mapHeightRef = useRef(1500);
  const sizeRef = useRef({ width: 800, height: 600 });
  const dprRef = useRef(window.devicePixelRatio || 1);
  const [dprVersion, setDprVersion] = useState(0);
  const gridSizeRef = useRef(DEFAULT_GRID_SIZE);
  const mapScaleRef = useRef(1.0);
  const bgColorRef = useRef<string | null>(null);
  const gridStyleRef = useRef<string>('lines');
  const gridOpacityRef = useRef<number>(100);
  const renderCallbacks = useRef<LayerCallback[]>([]);

  // ── Dirty-flag + RAF batching infrastructure ──
  const dirtyRef = useRef<DirtyFlags>({ ...INITIAL_DIRTY_FLAGS });
  const rafIdRef = useRef<number>(0);

  const setCamera = useCallback((cam: Camera) => {
    cameraRef.current = cam;
    setCameraState(cam);
  }, []);

  const renderMap = useCallback(() => {
    const canvas = refs.current.map;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const dpr = dprRef.current;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const c = cameraRef.current;
    const w = sizeRef.current.width;
    const h = sizeRef.current.height;
    ctx.clearRect(0, 0, w, h);
    const style = getComputedStyle(document.documentElement);
    const pageBg = bgColorRef.current;
    ctx.fillStyle = pageBg || style.getPropertyValue('--bg-dark').trim() || '#222';
    ctx.fillRect(0, 0, w, h);
    if (mapImageRef.current) {
      const scale = mapScaleRef.current;
      ctx.drawImage(mapImageRef.current, c.offsetX, c.offsetY, mapWidthRef.current * c.zoom * scale, mapHeightRef.current * c.zoom * scale);
    } else {
      ctx.fillStyle = pageBg || style.getPropertyValue('--bg-panel').trim() || '#2a2a3a';
      ctx.fillRect(c.offsetX, c.offsetY, mapWidthRef.current * c.zoom, mapHeightRef.current * c.zoom);
    }
  }, []);

  const renderGrid = useCallback(() => {
    const canvas = refs.current.grid;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const dpr = dprRef.current;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const c = cameraRef.current;
    const w = sizeRef.current.width;
    const h = sizeRef.current.height;
    ctx.clearRect(0, 0, w, h);

    // V004: Read grid style and opacity from settings refs
    const style = gridStyleRef.current;
    const opacity = gridOpacityRef.current;
    if (style === 'none' || opacity <= 0) return;

    const gs = gridSizeRef.current * c.zoom;
    if (gs < 5) return;
    const isLight = document.documentElement.getAttribute('data-theme') === 'light';
    const alpha = (opacity / 100) * (isLight ? 0.15 : 0.12);
    const color = isLight ? `rgba(0,0,0,${alpha})` : `rgba(255,255,255,${alpha})`;

    if (style === 'dots') {
      // Dot grid
      ctx.fillStyle = color;
      const dotRadius = Math.max(1, 1.5 * (opacity / 100));
      const startX = c.offsetX % gs;
      const startY = c.offsetY % gs;
      for (let x = startX; x < w; x += gs) {
        for (let y = startY; y < h; y += gs) {
          ctx.beginPath();
          ctx.arc(Math.round(x), Math.round(y), dotRadius, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    } else {
      // Lines grid (default)
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.beginPath();
      const startX = c.offsetX % gs;
      for (let x = startX; x < w; x += gs) {
        ctx.moveTo(Math.round(x) + 0.5, 0);
        ctx.lineTo(Math.round(x) + 0.5, h);
      }
      const startY = c.offsetY % gs;
      for (let y = startY; y < h; y += gs) {
        ctx.moveTo(0, Math.round(y) + 0.5);
        ctx.lineTo(w, Math.round(y) + 0.5);
      }
      ctx.stroke();
    }
  }, []);

  /** Internal: execute render for dirty layers only, then reset flags */
  const executeRender = useCallback(() => {
    const d = dirtyRef.current;
    if (d.map) { renderMap(); d.map = false; }
    if (d.grid) { renderGrid(); d.grid = false; }
    for (const cb of renderCallbacks.current) {
      if (d[cb.layer]) {
        cb.render();
      }
    }
    // Reset remaining layer flags that were handled by callbacks
    d.token = false;
    d.drawing = false;
    d.fog = false;
    d.ui = false;
  }, [renderMap, renderGrid]);

  const scheduleRender = useCallback(() => {
    if (rafIdRef.current) return; // already scheduled
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = 0;
      executeRender();
    });
  }, [executeRender]);

  const markDirty = useCallback((layer: LayerName) => {
    dirtyRef.current[layer] = true;
    scheduleRender();
  }, [scheduleRender]);

  const markAllDirty = useCallback(() => {
    const d = dirtyRef.current;
    d.map = d.grid = d.token = d.drawing = d.fog = d.ui = true;
    scheduleRender();
  }, [scheduleRender]);

  /** Backward-compatible render — marks all dirty and schedules RAF */
  const render = useCallback(() => {
    markAllDirty();
  }, [markAllDirty]);

  const resize = useCallback(() => {
    const container = containerNodeRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const w = Math.round(rect.width);
    const h = Math.round(rect.height);
    const dpr = Math.round((window.devicePixelRatio || 1) * 100) / 100;
    const prev = sizeRef.current;
    const prevDpr = dprRef.current;
    if (prev.width === w && prev.height === h && prevDpr === dpr) return;
    sizeRef.current = { width: w, height: h };
    dprRef.current = dpr;
    if (prevDpr !== dpr) {
      setDprVersion(v => v + 1);
    }
    for (const canvas of Object.values(refs.current)) {
      if (canvas) {
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
        canvas.style.width = w + 'px';
        canvas.style.height = h + 'px';
      }
    }
    // Render synchronously to avoid blank frame between resize and next RAF
    const d = dirtyRef.current;
    d.map = d.grid = d.token = d.drawing = d.fog = d.ui = true;
    executeRender();
  }, [executeRender]);

  useEffect(() => {
    if (!containerNode) return;
    resize();
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(resize);
    });
    ro.observe(containerNode);
    return () => ro.disconnect();
  }, [containerNode, resize]);

  const fitToView = useCallback(() => {
    const w = sizeRef.current.width;
    const h = sizeRef.current.height;
    const scaleX = w / mapWidthRef.current;
    const scaleY = h / mapHeightRef.current;
    const zoom = clamp(Math.min(scaleX, scaleY) * 0.95, 0.1, 5);
    const cam: Camera = {
      zoom,
      offsetX: (w - mapWidthRef.current * zoom) / 2,
      offsetY: (h - mapHeightRef.current * zoom) / 2,
    };
    setCamera(cam);
    markAllDirty();
  }, [setCamera, markAllDirty]);

  const setZoom = useCallback((newZoom: number) => {
    const z = clamp(newZoom, 0.1, 5);
    const cx = sizeRef.current.width / 2;
    const cy = sizeRef.current.height / 2;
    const old = cameraRef.current;
    const cam: Camera = {
      zoom: z,
      offsetX: cx - (cx - old.offsetX) * (z / old.zoom),
      offsetY: cy - (cy - old.offsetY) * (z / old.zoom),
    };
    setCamera(cam);
    markAllDirty();
  }, [setCamera, markAllDirty]);

  const loadMap = useCallback((src: string): Promise<void> => {
    const gen = ++mapLoadGenRef.current; // invalidate any in-flight loads
    if (!src) {
      mapImageRef.current = null;
      mapWidthRef.current = 2000;
      mapHeightRef.current = 1500;
      fitToView();
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        if (mapLoadGenRef.current !== gen) { resolve(); return; } // stale load — discard
        mapImageRef.current = img;
        mapWidthRef.current = img.width;
        mapHeightRef.current = img.height;
        fitToView();
        resolve();
      };
      img.onerror = () => {
        if (mapLoadGenRef.current !== gen) { resolve(); return; }
        mapImageRef.current = null;
        markAllDirty();
        resolve();
      };
      img.src = src;
    });
  }, [fitToView, markAllDirty]);

  const mouseToWorld = useCallback((e: React.MouseEvent | MouseEvent | React.PointerEvent | PointerEvent) => {
    const container = containerNodeRef.current;
    if (!container) return { x: 0, y: 0 };
    const rect = container.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    return screenToWorld(sx, sy, cameraRef.current);
  }, []);

  const snapToGridCenterFn = useCallback((wx: number, wy: number) => {
    const gs = gridSizeRef.current;
    const cellX = Math.floor(wx / gs);
    const cellY = Math.floor(wy / gs);
    return { x: cellX * gs + gs / 2, y: cellY * gs + gs / 2 };
  }, []);

  // Cleanup RAF on unmount
  useEffect(() => {
    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = 0;
      }
    };
  }, []);

  // Re-render canvas when theme changes
  useEffect(() => {
    const onThemeChange = () => markAllDirty();
    window.addEventListener('theme-changed', onThemeChange);
    return () => window.removeEventListener('theme-changed', onThemeChange);
  }, [markAllDirty]);

  return {
    camera,
    cameraRef,
    gridSize: gridSizeRef.current,
    mapWidth: mapWidthRef.current,
    mapHeight: mapHeightRef.current,
    width: sizeRef.current.width,
    height: sizeRef.current.height,
    dpr: dprRef.current,
    dprVersion,
    refs,
    containerRef,
    setCamera,
    render,
    markDirty,
    markAllDirty,
    requestRender: markAllDirty,
    loadMap,
    fitToView,
    setZoom,
    mouseToWorld,
    snapToGridCenter: snapToGridCenterFn,
    mapImage: mapImageRef.current,
    renderCallbacks,
    gridSizeRef,
    mapScaleRef,
    bgColorRef,
    gridStyleRef,
    gridOpacityRef,
  };
}
